import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { downloadFile } from '../../../Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { regenerateAuditedReport } from '../../../../store/dashboard/auditedFinancials/service';
import { AUDITED_REGENERATE_FAILED_TOAST } from '../../../../store/dashboard/auditedFinancials/constants';

export { AUDITED_REGENERATE_FAILED_TOAST } from '../../../../store/dashboard/auditedFinancials/constants';

interface UseAuditedReportDownloadProps {
    assesseeOrgId: string;
    orgName?: string;
}

interface UseAuditedReportDownloadResult {
    downloading: boolean;
    onDownloadClick: () => Promise<void>;
}

/**
 * Derives the save-as filename from the regenerate response.
 * Strips filesystem-hostile characters from orgName, then inserts it before
 * the extension: "<baseName> - <orgName><ext>". Falls back to the bare
 * resolvedFileName when orgName is empty.
 */
function buildSaveAsName(resolvedFileName: string, orgName: string): string {
    const sanitizedOrgName = orgName.replace(/[\\/:*?"<>|]/g, '');
    if (!sanitizedOrgName) return resolvedFileName;

    const lastDot = resolvedFileName.lastIndexOf('.');
    const base = lastDot !== -1 ? resolvedFileName.slice(0, lastDot) : resolvedFileName;
    const ext = lastDot !== -1 ? resolvedFileName.slice(lastDot) : '';
    return `${base} - ${sanitizedOrgName}${ext}`;
}

export function useAuditedReportDownload({ assesseeOrgId, orgName = '' }: UseAuditedReportDownloadProps): UseAuditedReportDownloadResult {
    // Ref-backed in-flight guard: prevents duplicate POSTs from rapid double-clicks.
    // A ref is used instead of the `downloading` state because state updates are
    // asynchronous — the ref is always current within the same event tick.
    const inFlightRef = useRef(false);

    // Render-facing spinner flag — updated alongside the ref so the UI stays in sync.
    const [downloading, setDownloading] = useState(false);

    const onDownloadClick = async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        setDownloading(true);
        try {
            const response = await regenerateAuditedReport(assesseeOrgId);
            const docId: string | undefined = response?.data?.data?.docId;
            const fileName: string | undefined = response?.data?.data?.fileName;

            if (!docId) {
                const serverMsg = response?.data?.responseData?.responseMessage;
                toast.error(serverMsg || AUDITED_REGENERATE_FAILED_TOAST);
                return;
            }

            // Guard: mnemosyne IDs are numeric; reject a non-numeric docId rather
            // than passing NaN to downloadFile.
            if (Number.isNaN(Number(docId))) {
                toast.error(AUDITED_REGENERATE_FAILED_TOAST);
                return;
            }

            const resolvedFileName = fileName ?? docId;
            const saveAsName = buildSaveAsName(resolvedFileName, orgName);

            await downloadFile(docId, undefined, saveAsName);
        } catch (err) {
            console.error('Failed to regenerate audited report:', err);
            toast.error(AUDITED_REGENERATE_FAILED_TOAST);
        } finally {
            inFlightRef.current = false;
            setDownloading(false);
        }
    };

    return { downloading, onDownloadClick };
}
